import { useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { GlassPanel } from '../components/ui/GlassPanel';
import { Button } from '../components/ui/Button';
import { Shield, Download, Trash2, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export function PrivacyPage() {
  const { user } = useAuth();
  const [requestType, setRequestType] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitDSAR = async (type: 'access' | 'export' | 'erasure') => {
    if (!user) return;

    setSubmitting(true);
    await supabase.from('dsar_requests').insert({
      user_id: user.id,
      request_type: type,
      status: 'pending',
      description: `User requested ${type} of their personal data`,
    });
    setSubmitting(false);
    setRequestType(null);
    alert(`Your ${type} request has been submitted. We will process it within 30 days.`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">GDPR & Privacy</h1>
          <p className="text-white/60">Manage your data and privacy settings</p>
        </div>

        <GlassPanel className="p-6">
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-primary-400 mr-3" />
            <h2 className="text-xl font-semibold text-white">Your Data Rights</h2>
          </div>
          <p className="text-white/60 mb-6">
            Under GDPR, you have the right to access, correct, export, and delete your personal data.
            Use the options below to exercise your rights.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="glass-panel p-4">
              <FileText className="w-8 h-8 text-primary-400 mb-3" />
              <h3 className="font-semibold text-white mb-2">Access Your Data</h3>
              <p className="text-sm text-white/60 mb-4">
                Request a copy of all personal data we hold about you
              </p>
              <Button
                variant="primary"
                size="sm"
                className="w-full"
                onClick={() => handleSubmitDSAR('access')}
                disabled={submitting}
              >
                Request Access
              </Button>
            </div>

            <div className="glass-panel p-4">
              <Download className="w-8 h-8 text-primary-400 mb-3" />
              <h3 className="font-semibold text-white mb-2">Export Your Data</h3>
              <p className="text-sm text-white/60 mb-4">
                Download your data in machine-readable format
              </p>
              <Button
                variant="primary"
                size="sm"
                className="w-full"
                onClick={() => handleSubmitDSAR('export')}
                disabled={submitting}
              >
                Request Export
              </Button>
            </div>

            <div className="glass-panel p-4">
              <Trash2 className="w-8 h-8 text-error-400 mb-3" />
              <h3 className="font-semibold text-white mb-2">Delete Your Data</h3>
              <p className="text-sm text-white/60 mb-4">
                Request permanent deletion of your personal data
              </p>
              <Button
                variant="error"
                size="sm"
                className="w-full"
                onClick={() => {
                  if (confirm('Are you sure you want to request data deletion? This action cannot be undone.')) {
                    handleSubmitDSAR('erasure');
                  }
                }}
                disabled={submitting}
              >
                Request Deletion
              </Button>
            </div>
          </div>
        </GlassPanel>

        <GlassPanel className="p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Privacy Information</h2>
          <div className="space-y-4 text-white/80">
            <div>
              <h3 className="font-medium text-white mb-2">Data Processing</h3>
              <p className="text-sm">
                We process your personal data in accordance with GDPR and EU data protection laws.
                All data is stored securely and processed only for legitimate business purposes.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-white mb-2">Your Rights</h3>
              <p className="text-sm">
                You have the right to access, rectify, erase, restrict processing, data portability,
                and to object to processing of your personal data. You can also withdraw consent at any time.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-white mb-2">Response Time</h3>
              <p className="text-sm">
                We will respond to your data subject requests within 30 days as required by GDPR Article 12.
              </p>
            </div>
          </div>
        </GlassPanel>
      </div>
    </DashboardLayout>
  );
}
